import { useState, useRef, useEffect } from "react";

// TODO use off the shelf throttle or move somehwere
function throttle<T extends (...args: Parameters<T>) => ReturnType<T>>(func: T, limit: number): T {
  let inThrottle: boolean;
  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  }) as T;
}

/**
 * Use this hook to manage focus on a table.
 * @param rowsLength The number of rows in the table.
 * @param ignoreFocusChildElements When the table row is focused and a button or other interactive element is clicked, the focus will be moved to the button or element.
 * When this happens we want to ignore actions from the table row like up/down arrow keys. But sometimes it can be useful to ignore the focus on the child and still interact with the table row.
 */
export function useTableFocus(
  rowsLength: number,
  ignoreFocusChildElements: boolean = false,
  allowFocusOnRows: boolean = false
) {
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  const [isTableFocused, setIsTableFocused] = useState(false);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const tableRef = useRef<HTMLTableElement>(null);

  // Scroll to row when focused
  useEffect(() => {
    if (!allowFocusOnRows) return;
    if (!isTableFocused) return;
    if (focusedRowIndex === -1) setFocusedRowIndex(0);

    const focusedRow = rowRefs.current[focusedRowIndex];
    if (focusedRow) {
      focusedRow.focus();
      focusedRow.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [focusedRowIndex, isTableFocused, allowFocusOnRows]);

  const handleFocusRow = (index: number, e?: React.KeyboardEvent | React.MouseEvent) => {
    if (!allowFocusOnRows) return;
    e?.preventDefault();
    setFocusedRowIndex(Math.max(0, Math.min(index, rowsLength - 1)));
  };

  const handleKeyDown = throttle((e: React.KeyboardEvent) => {
    if (!allowFocusOnRows) return;
    // For elements like buttons, we don't want to move focus to the table row if the button is a child of the table row
    if (!ignoreFocusChildElements && e.target !== e.currentTarget) {
      return;
    }

    if (e.key === "ArrowDown" || e.key === "Enter") {
      if (!ignoreFocusChildElements) {
        e.preventDefault();
      }
      setTimeout(() => {
        handleFocusRow(focusedRowIndex + 1);
      }, 100);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      handleFocusRow(focusedRowIndex - 1);
    }
  }, 10);

  return {
    focusedRowIndex,
    rowRefs,
    handleFocusRow,
    isTableFocused,
    setIsTableFocused,
    handleKeyDown,
    tableRef,
    getFocusedCellStyles: (index: number) => {
      if (!allowFocusOnRows) return {};

      return {
        transition: "background-color 0.3s ease",

        ...(index === focusedRowIndex
          ? {
              backgroundColor: "action.hover"
            }
          : {})
      };
    }
  };
}
